import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { CheckCircle2, Mail } from "lucide-react";

export const PriorityHubSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [checked, setChecked] = useState<string[]>([]);

  useEffect(() => {
    if (!isInView) return;
    const items = ["contract", "meeting"];
    const timers = items.map((id, i) =>
      setTimeout(() => setChecked((c) => [...c, id]), 1500 + i * 600)
    );
    return () => timers.forEach(clearTimeout);
  }, [isInView]);

  const noiseItems = [
    "Newsletter: Weekly digest",
    "Promo: 20% off...",
    "Notification: Someone liked...",
    "Spam: You've been selected...",
  ];
  const hubItems = [
    { id: "contract", title: "Contract Update", sub: "Legal team" },
    { id: "meeting", title: "Urgent Meeting", sub: "CEO" },
  ];

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 noise-texture" />
      <div className="container relative z-10" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Split-screen UI Left */}
          <motion.div
            className="relative order-2 lg:order-1"
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div className="grid grid-cols-2 gap-4">
              {/* Left: The Noise - blurred */}
              <div className="rounded-xl bg-white/5 border border-white/10 p-4 blur-[2px] select-none pointer-events-none opacity-60">
                <p className="text-xs text-muted-foreground mb-3 font-medium">The Noise</p>
                {noiseItems.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
                    <Mail className="w-3 h-3" />
                    {item}
                  </div>
                ))}
              </div>
              {/* Right: The Hub - crisp */}
              <motion.div
                className="rounded-xl bg-white/5 backdrop-blur-xl border border-white/10 p-4"
                initial={{ opacity: 0.8 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ duration: 0.5 }}
              >
                <p className="text-xs text-primary font-medium mb-3">Priority Hub</p>
                {hubItems.map((item) => (
                  <motion.div
                    key={item.id}
                    className="flex items-center gap-2 py-2.5 border-b border-white/5 last:border-0"
                    initial={{ opacity: 0, x: 10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.3 }}
                  >
                    {checked.includes(item.id) ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border border-white/20 flex-shrink-0" />
                    )}
                    <div>
                      <p className="text-sm font-medium">{item.title}</p>
                      <p className="text-xs text-muted-foreground">{item.sub}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>

          {/* Text Right */}
          <motion.div
            className="order-1 lg:order-2"
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 className="heading-lg mb-4">Your Executive Gatekeeper.</h2>
            <p className="text-lg text-primary font-medium mb-4">Silence the noise. Highlight the money.</p>
            <p className="body-lg text-muted-foreground">
              The Priority Hub filters out newsletters and low-value chatter. It presents you with &quot;High Value&quot; messages that require confirmation. Only see what moves the needle for your business.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
